import React, { useRef, useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import rehypeRaw from "rehype-raw";
import rehypeStringify from "rehype-stringify";
import ReactMarkdown from "react-markdown";
import classNames from "classnames";
import { NextSeo } from "next-seo";
import qs from "qs";
import { format } from "date-fns";
import es from "date-fns/locale/es/index.js";
import apiClient from "@/utils/apiClient";
import { ArrowUpIcon } from "@heroicons/react/outline";
import Link from "next/link";
import { Nav } from "@/components/navigation/Navbar";
import * as Text from "@/components/text";
import { localeTexts } from '@/utils/locale';

export default function Index({ entrada, locale }) {
  const router = useRouter();
  const topRef = useRef(null);
  const [entradaData, setEntradaData] = React.useState({...entrada.attributes});

     useEffect(() => {
      if (locale === "en" && entrada.attributes.localizations?.data?.length > 0) {
        // Find the English localization
        const englishData = entrada.attributes.localizations.data.find(
          (localization) => localization.attributes.locale === "en"
        );
  
        if (englishData) {
          setEntradaData({
            ...entrada.attributes, // Preserve other attributes
            ...englishData.attributes, // Override with English attributes
          });
        }
      } else {
        // Reset to the default locale (original attributes)
        setEntradaData({ ...entrada.attributes });
      }
    }, [locale, entrada.attributes]);

  
  return (
    <>
      <NextSeo
        title={entradaData.titulo}
        description={entradaData.subtitulo}
        canonical={`https://ambulante.org/blog/${entrada.attributes.slug}`}
        openGraph={{
          url: `https://ambulante.org/blog/${entrada.attributes.slug}`,
          title: entradaData.titulo,
          description: entradaData.subtitulo || "",
          images: [
            {
              url: entrada.attributes.cover_image.data.attributes.url,
              width: 800,
              height: 600,
              alt: entrada.attributes.titulo,
            },
          ],
          site_name: "Ambulante",
        }}
      />
      <div ref={topRef} className="sticky top-0 z-10 ">
        <div className="bg-neutral-50 py-3 ">
          <div className="amb-container">
            <ul className="flex items-center gap-5">
              <li className="flex space-x-2">
                {/* <Text.Link
                  label={localeTexts[locale].atras}
                  onClick={() => router.back()}
                  size="md"
                  className="hidden flex-none items-center align-middle text-neutral-1000 md:flex"
                ></Text.Link> */}
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/">
                  <Text.Link
                    className="text-neutral-1000"
                    size="md"
                    label={localeTexts[locale].inicio}
                  ></Text.Link>
                </Link>
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/blog">
                  <Text.Link
                    className="text-neutral-1000"
                    size="md"
                    label="Blog"
                  ></Text.Link>
                </Link>
              </li>
              <li>
                <Text.Body
                  className="line-clamp-1 overflow-hidden text-ellipsis break-normal font-light text-neutral-500"
                  size="lg"
                >
                  {entradaData.titulo}
                </Text.Body>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <img
          src={entrada.attributes.cover_image.data.attributes.url}
          className="relative h-[320px] w-full rounded-bl-[150px] object-cover lg:h-[526px] "
          alt=""
        ></img>
      </div>

      <div className="amb-container">
        <div className="mt-8 grid-cols-4 gap-16 lg:grid">
          <div className="col-span-3 ">
            <div className="space-y-3">
              <div className="border-b-2 border-b-black">
                {entrada.attributes.categorias.data.length > 0 ? (
                  <span className="bg-black p-1 px-3 uppercase text-white">
                    {entrada.attributes.categorias.data[0].attributes.name}
                  </span>
                ) : null}
                {entrada.attributes.autor && (
                  <span> {localeTexts[locale].por} {entrada.attributes.autor} &#9632; &nbsp;</span>
                )}
                <span>
                  {format(
                    new Date(entrada.attributes.publishedAt),
                    "dd MMM YYY",
                    {
                      locale: es,
                    }
                  )}
                </span>
              </div>
              <div>
                <Text.Headline className="font-bold">
                  {entradaData.titulo}
                </Text.Headline>
              </div>
              <div>
                <Text.Body size="xl" className="font-semibold">
                  {entradaData.subtitulo}
                </Text.Body>
              </div>
              <div className="sticky top-0 hidden space-y-3 md:block lg:hidden">
                {entrada.attributes.temas.data.length > 0 ? (
                  <div>
                    <Text.Overline size="md" className="text-neutral-600">
                      Temáticas
                    </Text.Overline>
                    <ul className="mt-2 flex flex-wrap gap-x-2 gap-y-2">
                      {entrada.attributes.temas.data.map((topic, i) => (
                        <li
                          key={topic.id}
                          className="flex flex-none items-center rounded-full bg-neutral-100 px-3"
                        >
                          <Text.Body className="text-neutral-900" size="lg">
                            &nbsp;
                            {topic.attributes.name}&nbsp;
                          </Text.Body>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {entrada.attributes.iniciativas.data.length > 0 ? (
                  <div>
                    <Text.Overline size="md" className="text-neutral-600">
                      {localeTexts[locale].programas}
                    </Text.Overline>
                    <ul className="mt-2 space-y-2 ">
                      {entrada.attributes.iniciativas.data.map(
                        (initiative, i) => (
                          <li key={initiative.id} className=" px-3 ">
                            <div
                              className={classNames(
                                {
                                  "ambulante presenta": "bg-presenta",
                                  ediciones: "bg-ediciones",
                                  "gira ambulante": "bg-gira",
                                  "mas-alla": "bg-mas-alla",
                                }[initiative.attributes.name.toLowerCase()],
                                "inline-block h-3 w-3 rounded-full"
                              )}
                            ></div>
                            <Text.Body
                              className="ml-2 inline-block border-b-2 border-neutral-300 text-neutral-900"
                              size="lg"
                            >
                              &nbsp;
                              {initiative.attributes.name}&nbsp;
                            </Text.Body>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div className="space-y-2">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw, rehypeStringify]}
                  className="font-body prose mt-10 prose-h2:uppercase lg:max-w-[100ch]"
                >
                  {entradaData.rich_text}
                </ReactMarkdown>

                {entrada.attributes?.podcast_embed && (
                  <div
                    className="mt-3"
                    dangerouslySetInnerHTML={{
                      __html: entrada.attributes.podcast_embed,
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
          <div className="col-span-1 md:hidden lg:block">
            <div className="sticky top-0 space-y-3">
              {entrada.attributes.temas.data.length > 0 ? (
                <div>
                  <Text.Overline size="md" className="text-neutral-600">
                    Temáticas
                  </Text.Overline>
                  <ul className="mt-2 flex flex-wrap gap-x-2 gap-y-2">
                    {entrada.attributes.temas.data.map((topic, i) => (
                      <li
                        key={topic.id}
                        className="flex flex-none items-center rounded-full bg-neutral-100 px-3"
                      >
                        <Text.Body className="text-neutral-900" size="lg">
                          &nbsp;
                          {topic.attributes.name}&nbsp;
                        </Text.Body>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {entrada.attributes.iniciativas.data.length > 0 ? (
                <div>
                  <Text.Overline size="md" className="text-neutral-600">
                    {localeTexts[locale].programas}
                  </Text.Overline>
                  <ul className="mt-2 space-y-2 ">
                    {entrada.attributes.iniciativas.data.map(
                      (initiative, i) => (
                        <li key={initiative.id} className=" px-3 ">
                          <div
                            className={classNames(
                              {
                                "ambulante presenta": "bg-presenta",
                                ediciones: "bg-ediciones",
                                "gira ambulante": "bg-gira",
                                "mas-alla": "bg-mas-alla",
                              }[initiative.attributes.name.toLowerCase()],
                              "inline-block h-3 w-3 rounded-full"
                            )}
                          ></div>
                          <Text.Body
                            className="ml-2 inline-block border-b-2 border-neutral-300 text-neutral-900"
                            size="lg"
                          >
                            &nbsp;
                            {initiative.attributes.name}&nbsp;
                          </Text.Body>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="amb-container my-10 text-center ">
          <Text.Link
            onClick={() =>
              topRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest",
              })
            }
            label={localeTexts[locale].volver}
            trailingIcon={<ArrowUpIcon />}
            size="lg"
            className="m-auto text-center"
          >
            &nbsp;
          </Text.Link>
        </div>
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  try {
    const { id: slug } = context.params;
    const {locale} = context.query;
    const requestedLocale = locale || "es-MX";
    const fallbackLocale = "es-MX";

    // Try to fetch content in the requested locale
    let query = qs.stringify({
      populate: "*",
      localizations:"*",
      locale: requestedLocale,
      filters: {
        slug: {
          $eqi: slug,
        },
      },
    });

    let entry = await apiClient.get(`/api/entradas?${query}`);
    let finalLocale = requestedLocale;

    // If content not found in requested locale and it's not already the fallback locale, try fallback
    if ((!entry.data.data[0] || !entry.data.data[0].attributes) && requestedLocale !== fallbackLocale) {
      query = qs.stringify({
        populate: "*",
        localizations:"*",
        locale: fallbackLocale,
        filters: {
          slug: {
            $eqi: slug,
          },
        },
      });

      entry = await apiClient.get(`/api/entradas?${query}`);
      finalLocale = fallbackLocale;
    }

    // If still not found, return 404
    if (!entry.data.data[0]) {
      throw new Error("Not found");
    }

    if (!entry.data.data[0].attributes) {
      throw new Error("Not found");
    }

    return {
      props: {
        entrada: entry.data.data[0],
        locale: finalLocale,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

Index.getLayout = function getLayout(page) {
  return (
    <div>
      <div className="amb-container bg-white">
        <Nav theme="light" />
      </div>
      <div>{page}</div>
    </div>
  );
};
